export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonObject | JsonValue[];
export type JsonObject = { [key: string]: JsonValue };

export const STORAGE_STORES = ["assets", "checkpoints", "projects", "named-checkpoints", "save-attempts", "disposable-cache", "placement-workspaces"] as const;
export type StorageStoreName = (typeof STORAGE_STORES)[number];
export type TransactionMode = "readonly" | "readwrite";

export interface StorageTransaction {
  get<T>(store: StorageStoreName, key: string): Promise<T | undefined>;
  getAll<T>(store: StorageStoreName): Promise<T[]>;
  put<T>(store: StorageStoreName, key: string, value: T): Promise<void>;
  delete(store: StorageStoreName, key: string): Promise<void>;
}

/** A deliberately small IndexedDB-shaped boundary; callers own retry policy. */
export interface StorageAdapter {
  transaction<T>(
    stores: readonly StorageStoreName[],
    mode: TransactionMode,
    operation: (transaction: StorageTransaction) => Promise<T>,
  ): Promise<T>;
  close(): void;
}

export interface AssetInput {
  id: string;
  mediaType: string;
  bytes: Uint8Array;
  sha256?: string;
}

export interface StoredAsset {
  id: string;
  mediaType: string;
  byteLength: number;
  sha256: string;
  bytes: Uint8Array;
  createdAt: string;
}

export interface ProjectMetadataInput {
  title: string;
  tags?: readonly string[];
  notes?: string;
  thumbnailAssetId?: string;
}

export interface ProjectRecord {
  id: string;
  title: string;
  normalizedTitle: string;
  tags: string[];
  normalizedTags: string[];
  notes: string;
  thumbnailAssetId?: string;
  headRevision: number;
  createdAt: string;
  updatedAt: string;
  lastOpenedAt?: string;
  archived: boolean;
  deletedAt?: string;
}

export interface DocumentCheckpoint {
  key: string;
  projectId: string;
  revision: number;
  createdAt: string;
  documentHash: string;
  document: JsonObject;
  assetIds: string[];
  complete: true;
  name?: string;
}

export interface ProjectSnapshot {
  project: ProjectRecord;
  checkpoint: DocumentCheckpoint;
  assets: StoredAsset[];
  recoveredFromRevision?: number;
}

export interface SaveCheckpointInput {
  projectId: string;
  revision: number;
  /** Required when advancing an existing project; omitted only for creation. */
  expectedHeadRevision?: number;
  metadata: ProjectMetadataInput;
  document: JsonObject;
  assetIds: readonly string[];
  assets?: readonly AssetInput[];
  checkpointName?: string;
  /** Require absence atomically when creating a conflict/bundle copy. */
  createOnly?: boolean;
  /** Caller-provided identity is useful for deterministic recovery tests. */
  attemptId?: string;
  now?: Date;
}

export interface LibraryQuery {
  text?: string;
  archived?: boolean;
  deleted?: boolean;
  sort?: "recent" | "alphabetical";
}

export interface StorageLimits {
  maxAssetBytes: number;
  maxAssetsPerProject: number;
  maxProjectBytes: number;
  maxDocumentJsonBytes: number;
  maxTitleLength: number;
  maxTags: number;
  maxTagLength: number;
  maxNotesLength: number;
}

export const DEFAULT_STORAGE_LIMITS: Readonly<StorageLimits> = Object.freeze({
  maxAssetBytes: 25 * 1024 * 1024,
  maxAssetsPerProject: 256,
  maxProjectBytes: 64 * 1024 * 1024,
  maxDocumentJsonBytes: 8 * 1024 * 1024,
  maxTitleLength: 160,
  maxTags: 32,
  maxTagLength: 64,
  maxNotesLength: 20_000,
});

export class StorageValidationError extends Error {
  readonly code = "STORAGE_VALIDATION";
}

export class StorageConflictError extends Error {
  readonly code = "STORAGE_CONFLICT";
}

export class StorageIntegrityError extends Error {
  readonly code = "STORAGE_INTEGRITY";
}

export const STORAGE_SCHEMA_VERSION = 3;
export const MAX_CHECKPOINT_NAME_LENGTH = 120;

export interface NamedCheckpoint {
  id: string;
  projectId: string;
  name: string;
  createdAt: string;
  purpose: "named" | "before-restore" | "before-flatten";
  checkpoint: DocumentCheckpoint;
  thumbnailAssetId?: string;
}

export interface SaveAttempt {
  id: string;
  projectId: string;
  revision: number;
  expectedHeadRevision?: number;
  documentHash: string;
  startedAt: string;
  status: "writing" | "complete" | "failed";
  completedAt?: string;
  failure?: "conflict" | "quota" | "integrity" | "write-failed";
}

export interface RecoveryCandidate {
  checkpoint: DocumentCheckpoint;
  valid: boolean;
  issues: string[];
}

export interface RecoveryReport {
  project?: ProjectRecord;
  candidates: RecoveryCandidate[];
  attempts: SaveAttempt[];
  selectedRevision?: number;
  /** Attempts without an intact completed checkpoint; metadata is evidence of unsaved work, not a recoverable document. */
  unrecoverableRevisions: number[];
}

/** A separate namespace guarantees cleanup cannot delete document/source assets. */
export interface DisposableCacheEntry {
  id: string;
  projectId?: string;
  kind: "thumbnail" | "preview" | "render";
  bytes: Uint8Array;
  createdAt: string;
  lastUsedAt: string;
}

export interface StorageEstimate {
  usageBytes?: number;
  quotaBytes?: number;
  availableBytes?: number;
  approximate: true;
  persistent?: boolean;
}

export interface StorageCapacityProvider {
  estimate(): Promise<{ usage?: number; quota?: number }>;
  persisted?(): Promise<boolean>;
  persist?(): Promise<boolean>;
}
